import * as THREE from "three";
import type { LobbyState, PlayerSnap } from "@genjiball/protocol";
import { RemotePlayer } from "../entities/remotePlayer.js";

export class LobbyScene {
  readonly scene: THREE.Scene;
  private players: Map<string, RemotePlayer> = new Map();
  private localPlayerId: string | null = null;

  constructor() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x141a24);
    this.scene.fog = new THREE.Fog(0x141a24, 40, 120);

    // Equirectangular skybox (one .jpg in /public).
    new THREE.TextureLoader().load(
      "/2310-w062-n005-290B-p15-290.jpg",
      (tex) => {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        tex.colorSpace = THREE.SRGBColorSpace;
        this.scene.background = tex;
        this.scene.environment = tex;
      },
    );

    // Sky-fill: cool blue from above, warm dark from below.
    this.scene.add(new THREE.HemisphereLight(0xbfd6ff, 0x2a1f15, 0.55));

    // Key light — sun. Casts shadows with a frustum sized for the platform.
    const sun = new THREE.DirectionalLight(0xfff1c8, 1.6);
    sun.position.set(18, 28, 12);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -22;
    sun.shadow.camera.right = 22;
    sun.shadow.camera.top = 22;
    sun.shadow.camera.bottom = -22;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 70;
    sun.shadow.bias = -0.0005;
    this.scene.add(sun);

    // Rim light from behind for shape definition.
    const rim = new THREE.DirectionalLight(0x88aaff, 0.5);
    rim.position.set(-15, 20, -12);
    this.scene.add(rim);

    const platform = new THREE.Mesh(
      new THREE.BoxGeometry(30, 30, 30),
      new THREE.MeshStandardMaterial({ color: 0x3d4855, roughness: 0.9 }),
    );
    platform.position.set(0, -15, 0);
    platform.receiveShadow = true;
    platform.castShadow = true;
    this.scene.add(platform);
  }

  setLocalPlayerId(id: string) {
    this.localPlayerId = id;
    // If the local player got created as a RemotePlayer before we knew, remove it.
    const stale = this.players.get(id);
    if (stale) {
      this.scene.remove(stale.group);
      stale.dispose();
      this.players.delete(id);
    }
  }

  syncLobby(lobby: LobbyState) {
    // Remove any players that have left.
    for (const id of this.players.keys()) {
      if (!lobby.players[id]) {
        const p = this.players.get(id)!;
        this.scene.remove(p.group);
        p.dispose();
        this.players.delete(id);
      }
    }
    // Add/update players — skip the local player (rendered separately as LocalPlayer).
    for (const lp of Object.values(lobby.players)) {
      if (lp.id === this.localPlayerId) continue;
      let p = this.players.get(lp.id);
      if (!p) {
        p = new RemotePlayer(lp.id, lp.name, lp.color, [0, 0, 0]);
        this.scene.add(p.group);
        this.players.set(lp.id, p);
      } else {
        p.applyMeta(lp.name, lp.color);
      }
    }
  }

  ingestSnapshot(serverTimeMs: number, players: PlayerSnap[]) {
    for (const s of players) {
      const p = this.players.get(s.id);
      if (!p) continue;
      p.ingestSnapshot(serverTimeMs, s.pos, s.yaw);
    }
  }

  update(renderTimeMs: number) {
    for (const p of this.players.values()) p.update(renderTimeMs);
  }

  getPlayerGroup(id: string): THREE.Object3D | null {
    return this.players.get(id)?.group ?? null;
  }
}
